import {useMemo} from 'react';
import {useNavigate, useParams} from 'react-router';
import type {StudyContent, StudyStatus} from '@cp/model';
import {env} from '../../config/env';
import useAddStudyProgress from '../../hooks/userProgress/useAddStudyProgress';
import useGetExamModule from '../../hooks/examModules/useGetExamModule';
import {useGetStudy} from '../../hooks/study/useGetStudy';
import useGetUserProgress from '../../hooks/userProgress/useGetUserProgress';
import {useUpdateStudyStatusOnly} from '../../hooks/study/useUpdateStudyStatusOnly';
import useDataNavigation from '../../hooks/helper/useDataNavigation';

export interface UseStudyPageReturn {
  // Data
  examModule: ReturnType<typeof useGetExamModule>['examModule'];
  study: ReturnType<typeof useGetStudy>['study'];
  sections: StudyContent[];
  toc: Array<{id: string; title: string; index: number}>;
  currentSection: StudyContent | undefined;
  currentSectionIndex: number;
  currentStudyProgressDone: boolean | undefined;
  subjectColor: {bg: string; border: string; icon: string};

  // Loading states
  moduleLoading: boolean;
  studyLoading: boolean;
  statusUpdating: boolean;
  progressUpdating: boolean;

  // Errors
  moduleError: Error | undefined;
  studyError: Error | undefined;

  // Navigation
  handleNext: () => void;
  handlePrevious: () => void;
  handleGoto: (index: number) => void;

  // Actions
  handleStatusChange: (status: StudyStatus) => Promise<void>;
  handleMarkAsDone: () => Promise<void>;
  handleBack: () => void;
  handleEdit: () => void;
}

export default function useStudyPage(): UseStudyPageReturn {
  const {examModuleId, topic} = useParams<{examModuleId: string; topic: string}>();
  const navigate = useNavigate();

  const {
    examModule,
    loading: moduleLoading,
    error: moduleError,
  } = useGetExamModule(examModuleId ?? '', {skip: !examModuleId});

  const {
    study,
    loading: studyLoading,
    error: studyError,
  } = useGetStudy({
    moduleId: examModuleId || '',
    topic: topic || '',
  });

  const {updateStudyStatusOnly, loading: statusUpdating} = useUpdateStudyStatusOnly();
  const {addStudyProgress, loading: progressUpdating} = useAddStudyProgress();
  const {progress: userProgress} = useGetUserProgress({
    examModuleId: examModuleId || '',
    skip: !examModuleId,
  });

  const currentStudyProgress = userProgress?.study.find((s) => s.topic === topic);
  const currentStudyProgressDone = currentStudyProgress?.done;
  const completedTitles = useMemo(() => new Set(currentStudyProgress?.title || []), [currentStudyProgress?.title]);

  // Use studyContent directly from study
  const sections: StudyContent[] = useMemo(() => {
    if (!study?.studyContent) return [];
    return study.studyContent;
  }, [study?.studyContent]);

  // Extract TOC from sections with completion status
  const toc = useMemo(
    () =>
      sections.map((section, index) => ({
        id: `section-${index}`,
        title: section.title,
        index,
        completed: completedTitles.has(section.title),
      })),
    [sections, completedTitles],
  );

  // Use data navigation hook for section navigation
  const {
    currentData: currentSection,
    currentIndex: currentSectionIndex,
    next: handleNext,
    previous: handlePrevious,
    goto: handleGoto,
  } = useDataNavigation(sections, {
    initialIndexFinder: (data): number => {
      // Find the first section not in currentStudyProgress.title array
      if (currentStudyProgress?.title && data.length > 0) {
        const completedTitlesSet = new Set(currentStudyProgress.title);
        const firstIncompleteIndex = data.findIndex((section) => !completedTitlesSet.has(section.title));
        if (firstIncompleteIndex >= 0) {
          return firstIncompleteIndex;
        }
        // All sections are completed, default to last section
        return data.length - 1;
      }
      // No progress data, start from the beginning
      return 0;
    },
    handlePrevious: (current, _previous) => {
      // Track progress when navigating to previous section
      // Only add if title isn't already in the completed list
      if (examModuleId && topic && sections.length > 0) {
        const isAlreadyCompleted = currentStudyProgress?.title?.includes(current.title) ?? false;
        if (!isAlreadyCompleted) {
          // Calculate progress: current titles + new title (make unique), then divide by total sections
          const currentTitles = currentStudyProgress?.title || [];
          const uniqueTitles = Array.from(new Set([...currentTitles, current.title]));
          const progressValue = uniqueTitles.length / sections.length;
          addStudyProgress(examModuleId, topic, undefined, [current.title], progressValue).catch((err) => {
            console.error('Failed to track study progress for previous section:', err);
          });
        }
      }
    },
    handleNext: (current, _next) => {
      // Track progress when navigating to next section
      // Only add if title isn't already in the completed list
      if (examModuleId && topic && sections.length > 0) {
        const isAlreadyCompleted = currentStudyProgress?.title?.includes(current.title) ?? false;
        if (!isAlreadyCompleted) {
          // Calculate progress: current titles + new title (make unique), then divide by total sections
          const currentTitles = currentStudyProgress?.title || [];
          const uniqueTitles = Array.from(new Set([...currentTitles, current.title]));
          const progressValue = uniqueTitles.length / sections.length;
          addStudyProgress(examModuleId, topic, undefined, [current.title], progressValue).catch((err) => {
            console.error('Failed to track study progress:', err);
          });
        }
      }
    },
    handleGoto: (current, _target) => {
      // Track progress when jumping to a section
      // Only add if title isn't already in the completed list
      if (examModuleId && topic && sections.length > 0) {
        const isAlreadyCompleted = currentStudyProgress?.title?.includes(current.title) ?? false;
        if (!isAlreadyCompleted) {
          // Calculate progress: current titles + new title (make unique), then divide by total sections
          const currentTitles = currentStudyProgress?.title || [];
          const uniqueTitles = Array.from(new Set([...currentTitles, current.title]));
          const progressValue = uniqueTitles.length / sections.length;
          addStudyProgress(examModuleId, topic, undefined, [current.title], progressValue).catch((err) => {
            console.error('Failed to track study progress:', err);
          });
        }
      }
    },
  });

  const subjectColor =
    examModule?.subject && examModule.subject in env.subjects
      ? env.subjects[examModule.subject as keyof typeof env.subjects]
      : env.subjects.MATHEMATICS;

  const handleBack = () => {
    if (examModuleId) {
      navigate(`/course/${examModuleId}`);
    } else {
      navigate('/');
    }
  };

  const handleEdit = () => {
    if (examModuleId && topic) {
      navigate(`/admin/study/${examModuleId}/${encodeURIComponent(topic)}`);
    }
  };

  const handleStatusChange = async (status: StudyStatus) => {
    if (examModuleId && topic) {
      await updateStudyStatusOnly(examModuleId, topic, status);
    }
  };

  const handleMarkAsDone = async () => {
    if (examModuleId && topic) {
      const newDoneStatus = !currentStudyProgressDone;
      // Calculate progress based on completed titles, independent of done status
      const progressValue = sections.length > 0 ? (currentStudyProgress?.title?.length || 0) / sections.length : 0;
      addStudyProgress(examModuleId, topic, newDoneStatus, undefined, progressValue).catch((error) => {
        console.error('Failed to toggle study progress:', error);
      });
    }
  };

  return {
    // Data
    examModule,
    study,
    sections,
    toc,
    currentSection,
    currentSectionIndex,
    currentStudyProgressDone,
    subjectColor,

    // Loading states
    moduleLoading,
    studyLoading,
    statusUpdating,
    progressUpdating,

    // Errors
    moduleError,
    studyError,

    // Navigation
    handleNext,
    handlePrevious,
    handleGoto,

    // Actions
    handleStatusChange,
    handleMarkAsDone,
    handleBack,
    handleEdit,
  };
}
